import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  alpha,
  Box,
  Button,
  ButtonBase,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import PersonRemoveOutlinedIcon from '@mui/icons-material/PersonRemoveOutlined';
import ReplyOutlinedIcon from '@mui/icons-material/ReplyOutlined';
import CloseIcon from '@mui/icons-material/Close';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api';
import API_BASE_URL from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import VirtualMeetingsPanel from '../components/discussionRooms/VirtualMeetingsPanel';
import WaecVirtualRegistrationDialog from '../components/discussionRooms/WaecVirtualRegistrationDialog';

interface Room {
  roomKey: string;
  examType: 'WAEC' | 'JAMB';
  subjectName: string;
  departments: string[];
  canEnter: boolean;
  isModerator: boolean;
  accessReason: string;
}

interface UpcomingMeeting {
  id: string;
  roomKey: string;
  title: string;
  scheduledStartAt: string;
  status: 'scheduled' | 'live';
}

interface DiscussionMessage {
  _id: string;
  userId: string;
  userName: string;
  content: string;
  role: 'participant' | 'moderator' | 'admin';
  imageUrl?: string;
  imageObjectKey?: string;
  legacyImageUrl?: string;
  allowDownload?: boolean;
  replyTo?: { messageId: string; userName: string; content: string };
  mentions?: Array<{ participantId: string; name: string }>;
  createdAt: string;
}

interface Participant {
  participantId: string;
  name: string;
  role: 'participant' | 'moderator' | 'admin';
}

const coordinatorSessionKey = (roomKey: string) => `discussion-room-coordinator:${roomKey}`;

const coordinatorRequestConfig = (roomKey: string) => {
  const coordinatorToken = sessionStorage.getItem(coordinatorSessionKey(roomKey));
  return coordinatorToken ? { headers: { 'x-discussion-coordinator-token': coordinatorToken } } : undefined;
};

const getDiscussionImageFallbackUrl = (objectKey?: string) => {
  if (!objectKey || !/^(?:uploads\/)?discussion-rooms\/[a-zA-Z0-9._/-]+$/.test(objectKey)) return '';
  const encodedKey = objectKey.split('/').map(encodeURIComponent).join('/');
  return `${API_BASE_URL.replace(/\/$/, '')}/uploads/${encodedKey}`;
};

const getLegacyDiscussionImageUrl = (roomKey: string, messageId: string) => (
  `${API_BASE_URL.replace(/\/$/, '')}/discussion-rooms/${encodeURIComponent(roomKey)}/images/${encodeURIComponent(messageId)}`
);

const ROOM_RULES = [
  'Keep every message relevant to the subject and respectful of other learners.',
  'Do not share personal information, abusive content, spam, or exam answers during a live assessment.',
  'Follow coordinator and moderator directions. Repeated or serious violations can result in immediate removal.',
  'Room messages are visible to other room members and may be removed during moderation.',
];

const formatCountdown = (milliseconds: number) => {
  if (milliseconds <= 0) return 'Starting now';
  const totalSeconds = Math.floor(milliseconds / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const clock = [hours, minutes, seconds].map((value) => String(value).padStart(2, '0')).join(':');
  return days > 0 ? `${days}d ${clock}` : clock;
};

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const DiscussionRooms: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, isLoading: authLoading, user } = useAuth();
  const roomFromUrl = searchParams.get('room') || '';
  const sessionRoomFromUrl = searchParams.get('sessionRoom') || '';
  const adminRoomAccess = Boolean(location.state?.adminRoomAccess);
  const hasSignedInSession = isAuthenticated || Boolean(localStorage.getItem('admin_auth_token')) || adminRoomAccess;
  const coordinatorToken = roomFromUrl ? sessionStorage.getItem(coordinatorSessionKey(roomFromUrl)) : null;
  const isCoordinatorRoom = Boolean(roomFromUrl && coordinatorToken);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [upcomingMeetings, setUpcomingMeetings] = useState<UpcomingMeeting[]>([]);
  const [upcomingError, setUpcomingError] = useState('');
  const [countdownNow, setCountdownNow] = useState(Date.now());
  const [examType, setExamType] = useState<'WAEC' | 'JAMB'>('WAEC');
  const [loadingRooms, setLoadingRooms] = useState(true);
  const [pageError, setPageError] = useState('');
  const roomRefreshErrorRef = React.useRef('');
  const [selectedRoom, setSelectedRoom] = useState<Room | null>(null);
  const [roomPanelTab, setRoomPanelTab] = useState<'chat' | 'meetings'>('chat');
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [waecRegistrationOpen, setWaecRegistrationOpen] = useState(false);
  const [roomToEnter, setRoomToEnter] = useState<Room | null>(null);
  const [coordinatorId, setCoordinatorId] = useState('');
  const [coordinatorName, setCoordinatorName] = useState('');
  const [rulesAccepted, setRulesAccepted] = useState(false);
  const [entering, setEntering] = useState(false);
  const [messages, setMessages] = useState<DiscussionMessage[]>([]);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [canModerate, setCanModerate] = useState(false);
  const [messageDraft, setMessageDraft] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageDownloadAllowed, setImageDownloadAllowed] = useState(false);
  const [imageViewerMessage, setImageViewerMessage] = useState<DiscussionMessage | null>(null);
  const [replyTo, setReplyTo] = useState<DiscussionMessage | null>(null);
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [mentionStart, setMentionStart] = useState<number | null>(null);
  const [mentionedParticipantIds, setMentionedParticipantIds] = useState<string[]>([]);
  const messageInputRef = React.useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const imageInputRef = React.useRef<HTMLInputElement | null>(null);
  const caretPositionRef = React.useRef(0);
  const swipeStartRef = React.useRef<{ x: number; y: number } | null>(null);
  const [sending, setSending] = useState(false);
  const isAdminUser = user?.role === 'admin' || Boolean(localStorage.getItem('admin_auth_token'));

  const fetchRooms = useCallback(async () => {
    if (authLoading && !isCoordinatorRoom) return;
    setLoadingRooms(true);
    setPageError('');
    try {
      const response = await api.get(adminRoomAccess ? '/admin/discussion-rooms' : '/discussion-rooms');
      const roomData = Array.isArray(response.data?.data) ? response.data.data : [];
      const availableRooms: Room[] = adminRoomAccess
        ? roomData.map((room: any) => ({ ...room, canEnter: true, isModerator: false, accessReason: '' }))
        : roomData;
      const coordinatorRoom = isCoordinatorRoom ? availableRooms.find((room) => room.roomKey === roomFromUrl) : null;
      if (isCoordinatorRoom) {
        if (!coordinatorRoom) {
          navigate('/discussion-rooms/coordinator-login', { replace: true });
          return;
        }
        setRooms([coordinatorRoom]);
        setExamType(coordinatorRoom.examType);
        setSelectedRoom(coordinatorRoom);
        return;
      }

      if (!hasSignedInSession && !authLoading) {
        navigate('/discussion-rooms/coordinator-login', { replace: true });
        return;
      }

      setRooms(availableRooms);
    } catch (error: any) {
      setPageError(error?.response?.data?.message || 'Discussion rooms could not be loaded. Sign in with a high-school account to continue.');
    } finally {
      setLoadingRooms(false);
    }
  }, [adminRoomAccess, authLoading, hasSignedInSession, isCoordinatorRoom, navigate, roomFromUrl]);

  useEffect(() => {
    void fetchRooms();
  }, [fetchRooms]);

  const visibleRooms = useMemo(() => (
    isCoordinatorRoom ? rooms : rooms.filter((room) => room.examType === examType)
  ), [examType, isCoordinatorRoom, rooms]);

  useEffect(() => {
    if (!isAuthenticated || authLoading || isCoordinatorRoom || selectedRoom) return undefined;
    let active = true;
    const loadUpcomingMeetings = async () => {
      try {
        const response = await api.get('/discussion-rooms/virtual-meetings/upcoming');
        if (!active) return;
        setUpcomingMeetings(Array.isArray(response.data?.data) ? response.data.data : []);
        setUpcomingError('');
      } catch (error: any) {
        if (active) {
          setUpcomingError(error?.response?.data?.message || 'Scheduled virtual sessions could not be loaded.');
        }
      }
    };
    void loadUpcomingMeetings();
    const refreshTimer = window.setInterval(() => { void loadUpcomingMeetings(); }, 15000);
    const countdownTimer = window.setInterval(() => setCountdownNow(Date.now()), 1000);
    const updateCountdownWhenVisible = () => {
      if (document.visibilityState === 'visible') setCountdownNow(Date.now());
    };
    document.addEventListener('visibilitychange', updateCountdownWhenVisible);
    return () => {
      active = false;
      window.clearInterval(refreshTimer);
      window.clearInterval(countdownTimer);
      document.removeEventListener('visibilitychange', updateCountdownWhenVisible);
    };
  }, [authLoading, isAuthenticated, isCoordinatorRoom, selectedRoom]);

  useEffect(() => {
    if (sessionRoomFromUrl.toLowerCase().startsWith('waec-')) setExamType('WAEC');
    else if (sessionRoomFromUrl.toLowerCase().startsWith('jamb-')) setExamType('JAMB');
  }, [sessionRoomFromUrl]);

  const loadMessages = useCallback(async (roomKey: string) => {
    const response = await api.get(`/discussion-rooms/${encodeURIComponent(roomKey)}/messages`, coordinatorRequestConfig(roomKey));
    const data = response.data?.data;
    setMessages(Array.isArray(data?.messages)
      ? data.messages.map((message: DiscussionMessage) => ({
        ...message,
        imageUrl: getDiscussionImageFallbackUrl(message.imageObjectKey) || message.imageUrl,
        legacyImageUrl: message.imageObjectKey ? getLegacyDiscussionImageUrl(roomKey, message._id) : undefined,
      }))
      : []);
    setParticipants(Array.isArray(data?.participants) ? data.participants : []);
    setCanModerate(Boolean(data?.canModerate));
  }, []);

  useEffect(() => {
    if (!selectedRoom) return undefined;
    let mounted = true;
    let refreshInFlight = false;
    const membershipExpiredMessage = 'Review the room rules and enter the room again.';
    const refresh = async () => {
      if (refreshInFlight) return;
      refreshInFlight = true;
      try {
        await loadMessages(selectedRoom.roomKey);
        if (roomRefreshErrorRef.current) {
          setPageError((current) => current === roomRefreshErrorRef.current ? '' : current);
          roomRefreshErrorRef.current = '';
        }
      } catch (error: any) {
        if (!mounted) return;
        if (error?.response?.status === 403 && error?.response?.data?.message === membershipExpiredMessage) {
          try {
            await api.post(
              `/discussion-rooms/${encodeURIComponent(selectedRoom.roomKey)}/enter`,
              { acknowledgedRules: true },
              coordinatorRequestConfig(selectedRoom.roomKey)
            );
            await loadMessages(selectedRoom.roomKey);
            roomRefreshErrorRef.current = '';
            setPageError((current) => current === membershipExpiredMessage ? '' : current);
            return;
          } catch (renewError: any) {
            if (!mounted) return;
            setSelectedRoom(null);
            setPageError(renewError?.response?.data?.message || 'Your room access has ended. Review the room rules and enter again.');
            return;
          }
        }
        if (error?.response?.status === 403 || error?.response?.status === 404) {
          if (isCoordinatorRoom) {
            sessionStorage.removeItem(coordinatorSessionKey(selectedRoom.roomKey));
            navigate(`/discussion-rooms/coordinator-login?roomKey=${encodeURIComponent(selectedRoom.roomKey)}`, { replace: true });
            return;
          }
          setSelectedRoom(null);
          setPageError(error?.response?.data?.message || 'Your room access has ended.');
          return;
        }
        const message = error?.response?.data?.message || 'Unable to refresh this room.';
        roomRefreshErrorRef.current = message;
        setPageError(message);
      } finally {
        refreshInFlight = false;
      }
    };
    void refresh();
    const intervalId = window.setInterval(() => { void refresh(); }, 5000);
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') void refresh();
    };
    document.addEventListener('visibilitychange', refreshWhenVisible);
    return () => {
      mounted = false;
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
    };
  }, [isCoordinatorRoom, loadMessages, navigate, selectedRoom]);

  const openEntryDialog = (room: Room) => {
    setRoomToEnter(room);
    setCoordinatorId('');
    setCoordinatorName('');
    setRulesAccepted(false);
  };

  const enterRoom = async () => {
    if (!roomToEnter || !rulesAccepted) return;
    setEntering(true);
    setPageError('');
    try {
      const response = await api.post(`/discussion-rooms/${encodeURIComponent(roomToEnter.roomKey)}/enter`, {
        acknowledgedRules: true,
        coordinatorId: coordinatorId.trim(),
        displayName: coordinatorName.trim(),
      });
      if (response.data?.data?.coordinatorToken) {
        sessionStorage.setItem(coordinatorSessionKey(roomToEnter.roomKey), response.data.data.coordinatorToken);
      }
      setSelectedRoom(roomToEnter);
      setRoomPanelTab(sessionRoomFromUrl === roomToEnter.roomKey ? 'meetings' : 'chat');
      setRoomToEnter(null);
      setMessages([]);
      setParticipants([]);
      setMessageDraft('');
    } catch (error: any) {
      setPageError(error?.response?.data?.message || 'Unable to enter this discussion room.');
    } finally {
      setEntering(false);
    }
  };

  const sendMessage = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selectedRoom || (!messageDraft.trim() && !imageFile) || sending) return;
    setSending(true);
    try {
      let imageAttachment: { imageObjectKey?: string; imageMimeType?: string } = {};
      if (imageFile) {
        const formData = new FormData();
        formData.append('image', imageFile);
        const uploadResponse = await api.post(
          `/discussion-rooms/${encodeURIComponent(selectedRoom.roomKey)}/images`,
          formData,
          coordinatorRequestConfig(selectedRoom.roomKey)
        );
        imageAttachment = uploadResponse.data?.data || {};
      }
      await api.post(`/discussion-rooms/${encodeURIComponent(selectedRoom.roomKey)}/messages`, {
        content: messageDraft.trim(),
        replyToMessageId: replyTo?._id,
        mentionedParticipantIds,
        allowDownload: Boolean(imageFile && imageDownloadAllowed),
        ...imageAttachment,
      }, coordinatorRequestConfig(selectedRoom.roomKey));
      setMessageDraft('');
      setImageFile(null);
      setImageDownloadAllowed(false);
      setReplyTo(null);
      setMentionedParticipantIds([]);
      setMentionQuery(null);
      await loadMessages(selectedRoom.roomKey);
    } catch (error: any) {
      setPageError(error?.response?.data?.message || 'Your message could not be sent.');
    } finally {
      setSending(false);
    }
  };

  const selectImage = (file?: File) => {
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/gif', 'image/webp'].includes(file.type)) {
      setPageError('Choose a JPEG, PNG, GIF, or WebP image.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setPageError('Images must be 5 MB or smaller.');
      return;
    }
    setPageError('');
    setImageFile(file);
    setImageDownloadAllowed(false);
  };

  const deleteMessage = async (message: DiscussionMessage) => {
    if (!selectedRoom || !isAdminUser || !window.confirm('Delete this message for everyone in the room?')) return;
    try {
      await api.delete(`/discussion-rooms/${encodeURIComponent(selectedRoom.roomKey)}/messages/${message._id}`, coordinatorRequestConfig(selectedRoom.roomKey));
      await loadMessages(selectedRoom.roomKey);
    } catch (error: any) {
      setPageError(error?.response?.data?.message || 'Unable to delete this message.');
    }
  };

  const updateMentionQuery = (value: string, caretPosition: number) => {
    const atIndex = value.lastIndexOf('@', Math.max(0, caretPosition - 1));
    if (atIndex < 0 || (atIndex > 0 && !/\s/.test(value[atIndex - 1]))) {
      setMentionQuery(null);
      setMentionStart(null);
      return;
    }
    const query = value.slice(atIndex + 1, caretPosition);
    if (query.includes('@') || query.length > 50) {
      setMentionQuery(null);
      setMentionStart(null);
      return;
    }
    setMentionQuery(query);
    setMentionStart(atIndex);
  };

  const matchingParticipants = mentionQuery === null ? [] : participants.filter((participant) => (
    participant.name.toLowerCase().startsWith(mentionQuery.trim().toLowerCase())
  ));

  const handleDraftChange = (value: string, caretPosition: number) => {
    setMessageDraft(value);
    caretPositionRef.current = caretPosition;
    updateMentionQuery(value, caretPosition);
    setMentionedParticipantIds((current) => current.filter((participantId) => {
      const participant = participants.find((entry) => entry.participantId === participantId);
      return Boolean(participant && value.includes(`@${participant.name}`));
    }));
  };

  const selectMention = (participant: Participant) => {
    if (mentionStart === null) return;
    const before = messageDraft.slice(0, mentionStart);
    const after = messageDraft.slice(caretPositionRef.current);
    const inserted = `@${participant.name} `;
    const nextDraft = `${before}${inserted}${after}`;
    const nextCaret = before.length + inserted.length;
    setMessageDraft(nextDraft);
    setMentionedParticipantIds((current) => [...new Set([...current, participant.participantId])]);
    setMentionQuery(null);
    setMentionStart(null);
    caretPositionRef.current = nextCaret;
    window.requestAnimationFrame(() => {
      messageInputRef.current?.focus();
      messageInputRef.current?.setSelectionRange(nextCaret, nextCaret);
    });
  };

  const replyToMessage = (message: DiscussionMessage) => {
    setReplyTo(message);
    messageInputRef.current?.focus();
  };

  const beginMessageSwipe = (event: React.PointerEvent<HTMLElement>) => {
    if (event.button !== 0) return;
    swipeStartRef.current = { x: event.clientX, y: event.clientY };
  };

  const finishMessageSwipe = (message: DiscussionMessage, event: React.PointerEvent<HTMLElement>) => {
    const start = swipeStartRef.current;
    swipeStartRef.current = null;
    if (start && event.clientX - start.x >= 64 && Math.abs(event.clientY - start.y) < 70) {
      replyToMessage(message);
    }
  };

  const removeParticipant = async (participant: Participant) => {
    if (!selectedRoom) return;
    try {
      await api.delete(`/discussion-rooms/${encodeURIComponent(selectedRoom.roomKey)}/users/${encodeURIComponent(participant.participantId)}`, coordinatorRequestConfig(selectedRoom.roomKey));
      await loadMessages(selectedRoom.roomKey);
    } catch (error: any) {
      setPageError(error?.response?.data?.message || 'Unable to remove this participant.');
    }
  };

  const exitCoordinatorSession = () => {
    if (!selectedRoom) return;
    sessionStorage.removeItem(coordinatorSessionKey(selectedRoom.roomKey));
    navigate(`/discussion-rooms/coordinator-login?roomKey=${encodeURIComponent(selectedRoom.roomKey)}`);
  };

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 } }}>
      {!isCoordinatorRoom && (
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/')}>Dashboard</Button>
          {selectedRoom && (
            <Button startIcon={<ArrowBackIcon />} onClick={() => {
              setSelectedRoom(null);
              setMessages([]);
              setParticipants([]);
              setRoomPanelTab('chat');
              setRequestDialogOpen(false);
            }}>
              Back to discussion rooms
            </Button>
          )}
        </Stack>
      )}
      {isCoordinatorRoom ? (
        <Paper
          component="header"
          variant="outlined"
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: 2,
            flexWrap: 'wrap',
            p: { xs: 2, md: 2.5 },
            mb: 3,
            borderLeft: 4,
            borderLeftColor: 'primary.main',
          }}
        >
          <Box>
            <Typography variant="overline" color="primary.main" sx={{ fontWeight: 800, letterSpacing: '0.12em' }}>
              COORDINATOR WORKSPACE
            </Typography>
            <Typography component="h1" variant="h4" sx={{ fontWeight: 800 }}>
              {selectedRoom?.subjectName || 'Discussion room'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Coordinate this subject room, support learners, and manage its virtual meetings.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Chip icon={<ForumOutlinedIcon />} label={`${participants.length} in room`} variant="outlined" />
            <Button variant="contained" onClick={() => setRoomPanelTab('meetings')}>
              Meeting tools
            </Button>
          </Stack>
        </Paper>
      ) : (
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2, flexWrap: 'wrap', mb: 3 }}>
        <Box>
          <Typography variant="overline" color="primary.main" sx={{ fontWeight: 800, letterSpacing: '0.12em' }}>
            HIGH SCHOOL LEARNING
          </Typography>
          <Typography component="h1" variant="h4" sx={{ fontWeight: 800 }}>Discussion Room</Typography>
          {!isCoordinatorRoom && <Typography color="text.secondary" sx={{ mt: 0.75 }}>Subject-based conversations for WAEC and JAMB learners.</Typography>}
          {isAuthenticated && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {examType === 'WAEC'
                ? 'WAEC virtual subject choices control access to WAEC virtual-learning rooms and sessions.'
                : 'JAMB virtual-room access uses your existing JAMB subject registration, so there is no separate WAEC subject chooser.'}
            </Typography>
          )}
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          {!isCoordinatorRoom && examType === 'WAEC' && isAuthenticated && (
            <Button variant="outlined" onClick={() => setWaecRegistrationOpen(true)}>Choose WAEC virtual subjects</Button>
          )}
          <Chip icon={<ForumOutlinedIcon />} label={`${visibleRooms.length} ${examType} rooms`} variant="outlined" />
        </Stack>
      </Box>
      )}

      {!isCoordinatorRoom && (
        <Tabs value={examType} onChange={(_, value) => setExamType(value)} aria-label="Exam discussion rooms" sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tab value="WAEC" label="WAEC rooms" />
          <Tab value="JAMB" label="JAMB rooms" />
        </Tabs>
      )}

      {pageError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPageError('')}>{pageError}</Alert>}
      {upcomingError && !selectedRoom && <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setUpcomingError('')}>{upcomingError}</Alert>}

      {selectedRoom ? (
        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
          {!isCoordinatorRoom && <Box component="header" sx={{ px: { xs: 2, md: 3 }, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="overline" color="text.secondary">{selectedRoom.examType} SUBJECT ROOM</Typography>
              <Typography variant="h5" component="h2" sx={{ fontWeight: 700 }}>{selectedRoom.subjectName}</Typography>
            </Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip size="small" label={`${participants.length} here`} />
              {canModerate && <Chip size="small" color={isAdminUser ? 'primary' : 'warning'} label={isAdminUser ? 'Administrator' : 'Moderator'} />}
              <Button variant="outlined" onClick={() => {
                setSelectedRoom(null);
                setMessages([]);
                setRequestDialogOpen(false);
              }}>Leave room</Button>
            </Stack>
          </Box>}
          <Divider />
          <Tabs value={roomPanelTab} onChange={(_, value) => setRoomPanelTab(value)} aria-label="Discussion room activities">
            <Tab value="chat" label="Discussion" />
            <Tab value="meetings" label="Virtual meetings" />
          </Tabs>
          <Divider />
          {roomPanelTab === 'meetings' ? (
            <VirtualMeetingsPanel
              roomKey={selectedRoom.roomKey}
              subjectName={selectedRoom.subjectName}
              coordinator={isCoordinatorRoom}
              allowStudentRequests={user?.role !== 'admin' && !isCoordinatorRoom}
            />
          ) : (
          <>
          {isCoordinatorRoom ? (
            <Paper variant="outlined" sx={{ m: 2, p: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Coordinator tools</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Use the Virtual meetings tab to schedule or start sessions, review approved student requests, and manage live meetings.
              </Typography>
              <Button size="small" sx={{ mt: 1 }} onClick={() => setRoomPanelTab('meetings')}>
                Open coordinator meeting tools
              </Button>
            </Paper>
          ) : (
            <VirtualMeetingsPanel
              roomKey={selectedRoom.roomKey}
              subjectName={selectedRoom.subjectName}
              coordinator={false}
              allowStudentRequests={user?.role !== 'admin'}
              sessionsOnly
              requestDialogOpen={requestDialogOpen}
              onRequestDialogOpen={() => setRequestDialogOpen(true)}
              onRequestDialogClose={() => setRequestDialogOpen(false)}
            />
          )}
          <Divider />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 230px' }, minHeight: 420 }}>
            <Box sx={{ p: { xs: 2, md: 3 }, display: 'flex', flexDirection: 'column', minHeight: 420 }}>
              <Box aria-live="polite" sx={{ flex: 1, minHeight: 250, maxHeight: 520, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1.25, pb: 2 }}>
                {messages.length === 0 ? (
                  <Typography color="text.secondary" sx={{ m: 'auto', textAlign: 'center' }}>No messages yet. Start a subject-focused discussion.</Typography>
                ) : messages.map((message) => (
                  <Box
                    key={message._id}
                    onPointerDown={beginMessageSwipe}
                    onPointerUp={(event) => finishMessageSwipe(message, event)}
                    onPointerCancel={() => { swipeStartRef.current = null; }}
                    sx={{
                      position: 'relative',
                      maxWidth: 'min(100%, 680px)',
                      p: 1.5,
                      pr: 5,
                      borderRadius: 1.5,
                      bgcolor: message.role === 'admin'
                        ? alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.28 : 0.10)
                        : message.role === 'moderator'
                          ? alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.22 : 0.10)
                          : alpha(theme.palette.info.main, theme.palette.mode === 'dark' ? 0.20 : 0.08),
                      borderLeft: '3px solid',
                      borderColor: message.role === 'admin' ? 'primary.main' : message.role === 'moderator' ? 'warning.main' : 'info.main',
                      alignSelf: 'flex-start',
                      touchAction: 'pan-y',
                    }}
                  >
                    {message.replyTo && (
                      <Box sx={{ mb: 1, pl: 1, borderLeft: 2, borderColor: 'text.secondary', opacity: 0.8 }}>
                        <Typography variant="caption" sx={{ display: 'block', fontWeight: 700 }}>Reply to {message.replyTo.userName}</Typography>
                        <Typography variant="caption" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{message.replyTo.content}</Typography>
                      </Box>
                    )}
                    <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mb: 0.5 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ color: message.role === 'admin'
                          ? (theme.palette.mode === 'dark' ? 'primary.light' : 'primary.dark')
                          : message.role === 'moderator'
                            ? (theme.palette.mode === 'dark' ? 'warning.light' : 'warning.dark')
                            : (theme.palette.mode === 'dark' ? 'info.light' : 'info.dark') }}
                      >
                        {message.userName}
                        {message.role !== 'participant' && <Typography component="span" variant="caption" sx={{ ml: 0.75, fontWeight: 500 }}>{message.role === 'admin' ? 'Admin' : 'Moderator'}</Typography>}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">{new Date(message.createdAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</Typography>
                    </Stack>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                      {message.mentions?.length ? (() => {
                        const names = message.mentions.map((mention) => escapeRegExp(`@${mention.name}`));
                        const parts = message.content.split(new RegExp(`(${names.join('|')})`, 'gi'));
                        return parts.map((part, index) => {
                          const isMention = message.mentions?.some((mention) => part.toLowerCase() === `@${mention.name}`.toLowerCase());
                          return isMention
                            ? <Box component="span" key={index} sx={{ color: 'primary.main', fontWeight: 700 }}>{part}</Box>
                            : part;
                        });
                      })() : message.content}
                    </Typography>
                    {message.imageUrl && (
                      <ButtonBase
                        onClick={() => setImageViewerMessage(message)}
                        aria-label={`View image shared by ${message.userName}`}
                        sx={{ display: 'block', mt: 1, borderRadius: 1, cursor: 'zoom-in', overflow: 'hidden' }}
                      >
                        <Box
                          component="img"
                          src={message.imageUrl || getDiscussionImageFallbackUrl(message.imageObjectKey)}
                          alt={`Image shared by ${message.userName}. Click to view full size.`}
                          loading="lazy"
                          onError={(event) => {
                            const image = event.currentTarget;
                            const fallbackUrl = message.legacyImageUrl;
                            const fallbackAbsoluteUrl = fallbackUrl ? new URL(fallbackUrl, window.location.href).href : '';
                            if (fallbackAbsoluteUrl && !image.dataset.fallbackTried && image.src !== fallbackAbsoluteUrl) {
                              image.dataset.fallbackTried = 'true';
                              image.src = fallbackUrl as string;
                            } else {
                              image.style.display = 'none';
                            }
                          }}
                          sx={{ display: 'block', maxWidth: '100%', maxHeight: 320, objectFit: 'contain' }}
                        />
                      </ButtonBase>
                    )}
                    <Tooltip title="Reply to this message. You can also swipe right." placement="right">
                      <IconButton
                        size="small"
                        aria-label={`Reply to ${message.userName}'s message`}
                        onClick={() => replyToMessage(message)}
                        sx={{ position: 'absolute', right: 4, top: 4, color: 'text.secondary' }}
                      >
                        <ReplyOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {isAdminUser && (
                      <Tooltip title="Delete message for everyone">
                        <IconButton size="small" aria-label={`Delete message from ${message.userName}`} onClick={() => void deleteMessage(message)} sx={{ position: 'absolute', right: 4, bottom: 4, color: 'error.main' }}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                ))}
              </Box>
              <Box component="form" onSubmit={sendMessage} sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                <Box sx={{ flex: 1, minWidth: 0, position: 'relative' }}>
                  {replyTo && (
                    <Paper variant="outlined" sx={{ mb: 1, p: 1, display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'center' }}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700 }}>Replying to {replyTo.userName}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{replyTo.content}</Typography>
                      </Box>
                      <IconButton size="small" aria-label="Cancel reply" onClick={() => setReplyTo(null)}><CloseIcon fontSize="small" /></IconButton>
                    </Paper>
                  )}
                  {mentionQuery !== null && (
                    <Paper
                      role="listbox"
                      aria-label="Room participants to mention"
                      variant="outlined"
                      sx={{ position: 'absolute', zIndex: 2, bottom: 'calc(100% + 6px)', left: 0, right: 0, maxHeight: 220, overflowY: 'auto', boxShadow: 5 }}
                    >
                      {matchingParticipants.length ? matchingParticipants.map((participant) => (
                        <Button
                          key={participant.participantId}
                          role="option"
                          aria-selected="false"
                          fullWidth
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => selectMention(participant)}
                          sx={{ justifyContent: 'flex-start', textTransform: 'none', color: 'text.primary' }}
                        >
                          {participant.name} {participant.role === 'admin' ? '(Administrator)' : participant.role === 'moderator' ? '(Coordinator / moderator)' : '(Student)'}
                        </Button>
                      )) : (
                        <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1.5 }}>No matching participants are currently in the room.</Typography>
                      )}
                    </Paper>
                  )}
                  {imageFile && (
                    <Paper variant="outlined" sx={{ mb: 1, p: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="caption" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{imageFile.name}</Typography>
                      <FormControlLabel
                        control={<Checkbox size="small" checked={imageDownloadAllowed} onChange={(event) => setImageDownloadAllowed(event.target.checked)} />}
                        label={<Typography variant="caption">Allow downloads</Typography>}
                      />
                      <IconButton size="small" aria-label="Remove image attachment" onClick={() => { setImageFile(null); setImageDownloadAllowed(false); }}><CloseIcon fontSize="small" /></IconButton>
                    </Paper>
                  )}
                  <TextField
                    fullWidth
                    multiline
                    maxRows={4}
                    label="Message"
                    value={messageDraft}
                    inputRef={messageInputRef}
                    onChange={(event) => handleDraftChange(event.target.value, event.target.selectionStart || 0)}
                    onClick={() => { caretPositionRef.current = messageInputRef.current?.selectionStart || 0; updateMentionQuery(messageDraft, caretPositionRef.current); }}
                    onKeyUp={() => { caretPositionRef.current = messageInputRef.current?.selectionStart || 0; updateMentionQuery(messageDraft, caretPositionRef.current); }}
                    inputProps={{ maxLength: 2000, 'aria-autocomplete': 'list' }}
                    helperText={`${messageDraft.length}/2000`}
                  />
                </Box>
                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  hidden
                  onChange={(event) => selectImage(event.target.files?.[0])}
                />
                <Tooltip title="Attach image">
                  <IconButton type="button" aria-label="Attach image" onClick={() => imageInputRef.current?.click()} disabled={sending} sx={{ mb: 3, border: 1, borderColor: 'divider', borderRadius: 1.5, width: 44, height: 44 }}>
                    <ImageOutlinedIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Send message">
                  <span>
                    <IconButton type="submit" color="primary" aria-label="Send message" disabled={sending || (!messageDraft.trim() && !imageFile)} sx={{ mb: 3, border: 1, borderColor: 'divider', borderRadius: 1.5, width: 44, height: 44 }}>
                      {sending ? <CircularProgress size={20} /> : <SendOutlinedIcon />}
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>
            <Box sx={{ borderLeft: { md: 1 }, borderTop: { xs: 1, md: 0 }, borderColor: 'divider', p: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Participants</Typography>
              <Stack spacing={1} sx={{ mb: 2 }}>
                {participants.filter((participant) => participant.role === 'participant').map((participant) => (
                  <Box key={participant.participantId} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                    <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{participant.name}</Typography>
                    {canModerate && <Tooltip title={`Remove ${participant.name}`}><IconButton size="small" aria-label={`Remove ${participant.name}`} onClick={() => void removeParticipant(participant)}><PersonRemoveOutlinedIcon fontSize="small" /></IconButton></Tooltip>}
                  </Box>
                ))}
                {participants.every((participant) => participant.role !== 'participant') && <Typography variant="body2" color="text.secondary">No learners are here yet.</Typography>}
              </Stack>
              <Divider sx={{ mb: 1.5 }} />
              <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Coordinators &amp; moderators</Typography>
              <Stack spacing={1}>
                {participants.filter((participant) => participant.role === 'moderator').map((participant) => (
                  <Box key={participant.participantId} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                    <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{participant.name}</Typography>
                  </Box>
                ))}
                {participants.every((participant) => participant.role !== 'moderator') && <Typography variant="body2" color="text.secondary">No coordinators are here yet.</Typography>}
              </Stack>
              {participants.some((participant) => participant.role === 'admin') && (
                <>
                  <Divider sx={{ my: 1.5 }} />
                  <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Administrators</Typography>
                  <Stack spacing={1}>
                    {participants.filter((participant) => participant.role === 'admin').map((participant) => (
                      <Typography key={participant.participantId} variant="body2" sx={{ color: theme.palette.mode === 'dark' ? 'primary.light' : 'primary.dark', overflowWrap: 'anywhere' }}>
                        {participant.name}
                      </Typography>
                    ))}
                  </Stack>
                </>
              )}
            </Box>
          </Box>
          </>
          )}
          {isCoordinatorRoom && (
            <Box
              component="footer"
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', sm: 'center' },
                gap: 2,
                flexWrap: 'wrap',
                px: { xs: 2, md: 3 },
                py: 2,
                borderTop: 1,
                borderColor: 'divider',
                bgcolor: 'action.hover',
              }}
            >
              <Box>
                <Typography variant="subtitle2">Coordinator session active</Typography>
                <Typography variant="caption" color="text.secondary">
                  Your coordinator access applies to this discussion room. Exit when you have finished managing it.
                </Typography>
              </Box>
              <Button color="inherit" variant="outlined" onClick={exitCoordinatorSession}>
                Exit coordinator session
              </Button>
            </Box>
          )}
        </Paper>
      ) : loadingRooms ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : visibleRooms.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <Typography variant="h6">No {examType} subject rooms are available yet.</Typography>
          <Typography color="text.secondary" sx={{ mt: 1 }}>Rooms appear when WAEC and JAMB subjects are available.</Typography>
        </Paper>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 290px), 1fr))', gap: 2 }}>
          {visibleRooms.map((room) => (
            <Paper key={room.roomKey} variant="outlined" sx={{ p: 2.5, borderRadius: 2, display: 'flex', flexDirection: 'column', minHeight: 190 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                <Typography variant="h6" component="h2" sx={{ fontWeight: 700, overflowWrap: 'anywhere' }}>{room.subjectName}</Typography>
                <Chip size="small" label={room.examType} color={room.examType === 'WAEC' ? 'primary' : 'success'} variant="outlined" />
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, flex: 1 }}>
                {room.departments.length === 3 ? 'Open across departments' : `${room.departments.join(', ')} department`}
              </Typography>
              {(() => {
                const roomMeetings = upcomingMeetings
                  .filter((meeting) => meeting.roomKey === room.roomKey)
                  .sort((left, right) => (
                    (left.status === 'live' ? -1 : 0) - (right.status === 'live' ? -1 : 0)
                    || new Date(left.scheduledStartAt).getTime() - new Date(right.scheduledStartAt).getTime()
                  ));
                const nextMeeting = roomMeetings[0];
                if (!nextMeeting) return null;
                return (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.25,
                      mb: 1.25,
                      borderColor: nextMeeting.status === 'live' ? 'success.main' : 'primary.light',
                      bgcolor: nextMeeting.status === 'live' ? 'success.50' : 'primary.50',
                    }}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
                      <Typography variant="caption" sx={{ fontWeight: 700 }}>
                        {nextMeeting.status === 'live' ? 'LIVE SESSION' : 'UPCOMING SESSION'}
                      </Typography>
                      {roomMeetings.length > 1 && <Chip size="small" label={`+${roomMeetings.length - 1}`} />}
                    </Stack>
                    <Typography variant="body2" sx={{ mt: 0.25, fontWeight: 600, overflowWrap: 'anywhere' }}>
                      {nextMeeting.title}
                    </Typography>
                    <Typography
                      variant="caption"
                      color={nextMeeting.status === 'live' ? 'success.dark' : 'primary.dark'}
                      sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}
                    >
                      {nextMeeting.status === 'live'
                        ? 'Live now'
                        : formatCountdown(new Date(nextMeeting.scheduledStartAt).getTime() - countdownNow)}
                    </Typography>
                  </Paper>
                );
              })()}
              {!room.canEnter && <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>{room.accessReason}</Typography>}
              {!room.canEnter && room.accessReason.startsWith('Subscribe to Explore ') && (
                <Button
                  variant="outlined"
                  onClick={() => navigate('/payment', {
                    state: {
                      featureKey: room.examType.toLowerCase(),
                      featureLabel: `Explore ${room.examType}`,
                      selectedFeatures: [room.examType.toLowerCase()],
                    },
                  })}
                >
                  Subscribe to Explore {room.examType}
                </Button>
              )}
              {room.canEnter && room.isModerator && <Chip size="small" color="warning" label="Coordinator access" sx={{ alignSelf: 'flex-start', mb: 1 }} />}
              {room.canEnter && <Button variant="contained" onClick={() => openEntryDialog(room)}>Enter room</Button>}
            </Paper>
          ))}
        </Box>
      )}

      <Dialog open={Boolean(roomToEnter)} onClose={() => !entering && setRoomToEnter(null)} fullWidth maxWidth="sm">
        <DialogTitle>Before you enter{roomToEnter ? `: ${roomToEnter.subjectName}` : ''}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This is a {roomToEnter?.examType} subject room. Your participation is subject to these rules:
          </Typography>
          <Stack component="ol" spacing={1} sx={{ pl: 2.5, mb: 2 }}>
            {ROOM_RULES.map((rule) => <Typography component="li" variant="body2" key={rule}>{rule}</Typography>)}
          </Stack>
          {!roomToEnter?.canEnter && (
            <Stack spacing={2} sx={{ mb: 1 }}>
              <TextField
                fullWidth
                label="Coordinator ID"
                value={coordinatorId}
                onChange={(event) => setCoordinatorId(event.target.value)}
                autoComplete="off"
              />
              <TextField
                fullWidth
                label="Coordinator display name"
                value={coordinatorName}
                onChange={(event) => setCoordinatorName(event.target.value)}
                autoComplete="name"
                inputProps={{ maxLength: 80 }}
              />
            </Stack>
          )}
          <FormControlLabel
            control={<Checkbox checked={rulesAccepted} onChange={(event) => setRulesAccepted(event.target.checked)} />}
            label="I have read and agree to follow these room rules."
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRoomToEnter(null)} disabled={entering}>Cancel</Button>
          <Button variant="contained" onClick={() => void enterRoom()} disabled={!rulesAccepted || entering || (!roomToEnter?.canEnter && (!coordinatorId.trim() || !coordinatorName.trim()))}>
            {entering ? 'Entering…' : 'Accept and enter'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(imageViewerMessage)} onClose={() => setImageViewerMessage(null)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
          Image shared by {imageViewerMessage?.userName}
          <IconButton aria-label="Close image viewer" onClick={() => setImageViewerMessage(null)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          {(imageViewerMessage?.imageUrl || imageViewerMessage?.imageObjectKey) && (
            <Box
              component="img"
              src={getDiscussionImageFallbackUrl(imageViewerMessage.imageObjectKey) || imageViewerMessage.imageUrl}
              alt={`Image shared by ${imageViewerMessage.userName}`}
              onError={(event) => {
                const fallbackUrl = imageViewerMessage.legacyImageUrl;
                const fallbackAbsoluteUrl = fallbackUrl ? new URL(fallbackUrl, window.location.href).href : '';
                if (fallbackAbsoluteUrl && !event.currentTarget.dataset.fallbackTried && event.currentTarget.src !== fallbackAbsoluteUrl) {
                  event.currentTarget.dataset.fallbackTried = 'true';
                  event.currentTarget.src = fallbackUrl as string;
                }
              }}
              sx={{ display: 'block', maxWidth: '100%', maxHeight: '75vh', objectFit: 'contain' }}
            />
          )}
        </DialogContent>
        {selectedRoom && imageViewerMessage?.allowDownload && imageViewerMessage.imageUrl && (
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              component="a"
              href={`${getLegacyDiscussionImageUrl(selectedRoom.roomKey, imageViewerMessage._id)}?download=1`}
              download
              startIcon={<DownloadOutlinedIcon />}
            >
              Download image
            </Button>
          </DialogActions>
        )}
      </Dialog>
      <WaecVirtualRegistrationDialog open={waecRegistrationOpen} onClose={() => setWaecRegistrationOpen(false)} />
    </Container>
  );
};

export default DiscussionRooms;
